//SignUp.js
import React, { useState } from 'react';
import { BeatLoader } from 'react-spinners';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// notification
import { notifyError, notifySuccess, Notification } from '../Notification';

// set up amplify config
import { Amplify } from 'aws-amplify';
import { signUp, confirmSignUp, resendSignUpCode, fetchAuthSession } from 'aws-amplify/auth';
import awsconfig from '../../../src/aws-exports';

import '../../components/auth/auth.css';
import { api } from '../../api';

Amplify.configure(awsconfig);

const SignUp = ({ onClose, prefilledEmail, onSignUpComplete }) => {

    const [email, setEmail] = useState(prefilledEmail || '');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [confirmationCode, setConfirmationCode] = useState('');
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [loadingSignup, setLoadingSignup] = useState(false);
    const [loadingConfirmSignup, setLoadingConfirmSignup] = useState(false);

    const handleFirstNameChange = (e) => {
        setFirstName(e.target.value);
    }

    const handleLastNameChange = (e) => {
        setLastName(e.target.value);
    }
  
    const handleEmailChange = (e) => {
        setEmail(e.target.value);
    }

    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
    }

    // Helper function to get cognito_sub after successful sign up
    const getCognitoSub = async () => {
        try {
            const authSession = await fetchAuthSession();
            const cognitoSub = authSession?.tokens?.idToken?.payload?.sub;
            
            if (!cognitoSub) {
                console.warn('Unable to extract cognito_sub from auth session');
                return null;
            }
            
            console.log('✅ Successfully extracted cognito_sub:', cognitoSub);
            return cognitoSub;
        } catch (error) {
            console.error('Error getting cognito_sub:', error);
            return null;
        }
    };

    // Process pending invitation for new users (updated with cognito_sub)
    const processPendingInvitation = async (token, userEmail, userData, cognitoSub) => {
        try {
            console.log('🔄 Processing pending invitation for new user:', userEmail);
            
            // Use the public route that handles both user creation and invitation acceptance
            const response = await api.post('/invite/accept-new-user', {
                token: token,
                firstName: userData.firstName,
                lastName: userData.lastName,
                email: userEmail,
                cognitoSub: cognitoSub // Include cognito_sub
            });
            
            if (response.data.success) {
                notifySuccess(`Account created and invitation accepted! Welcome to ${response.data.data.businessName}.`);
                return true;
            }
        } catch (error) {
            console.error('Error processing pending invitation:', error);
            
            // Handle specific error cases
            if (error.response?.status === 409 && error.response?.data?.code === 'USER_ALREADY_EXISTS') {
                // User already has access to this business
                notifyError(error.response.data.message);
                return false;
            }
            
            if (error.response?.status === 400) {
                const errorMsg = error.response.data.message || 'Invalid invitation request';
                notifyError(errorMsg);
                return false;
            }
            
            // For other errors, try to add user normally
            console.log('Invitation processing failed, will add user normally...');
            notifyError('Invitation processing failed, but account will be created normally.');
            return false;
        }
    };

    async function resendConfirmationCode(username) {
        try {
            const response = await resendSignUpCode({ username: username });
            console.log(`signup response = ${JSON.stringify(response)}`);
            setIsConfirmed(true);     
        } catch (err) {
            let errorMsg = err.message;

            if (err.message === 'User is already confirmed.') {
                errorMsg = `User is already confirmed. You can sign in with this user.`;
                notifyError(errorMsg);
                onClose();
            }
            else {
                notifyError(errorMsg);
                console.log(errorMsg);
            }
        }
    }

    const handleSignUp = async (e) => {
        e.preventDefault();        
        try {
            setLoadingSignup(true);
            await signUp({
                username: email,
                password,
                options: {
                    userAttributes: {
                        email,
                        given_name: firstName,
                        family_name: lastName
                    },
                    autoSignIn: true
                },
            });
        
            setIsConfirmed(true);
            setLoadingSignup(false);      
        } catch (error) {
            console.error('Error signing up:', error);
            setIsConfirmed(false);
            setLoadingSignup(false);

            if (error.message === 'User already exists') {
                setLoadingSignup(true);
                await resendConfirmationCode(email);
                setLoadingSignup(false);
                console.log(error.message);
            }
            else {
                notifyError(error.message);
                console.log(error.message);
            }
        }
    };

    // utc format current MYSQL DATETIME 
    function getMysqlUtcDatetime() {
        const now = new Date();
    
        const year = now.getUTCFullYear();
        const month = (now.getUTCMonth() + 1).toString().padStart(2, '0');
        const day = now.getUTCDate().toString().padStart(2, '0');
        const hours = now.getUTCHours().toString().padStart(2, '0');
        const minutes = now.getUTCMinutes().toString().padStart(2, '0');
        const seconds = now.getUTCSeconds().toString().padStart(2, '0');
    
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }

    // add new user to users table (updated to include cognito_sub)
    const addNewUser = async (userData) => {
        try {
            const response = await api.post('/user/add', userData);
            console.log('✅ User added to database:', response.data);
            return true;
        } catch (error) {
            console.error('❌ Failed to add user:', error.response ? error.response.data : error.message);
            
            // Check if this is a "user already exists" error
            if (error.response?.status === 409 || error.response?.data?.message?.includes('already exists')) {
                console.log('User already exists in database, continuing...');
                return true; // Not really an error for our flow
            }
            
            return false;
        }
    }

    // Updated handleConfirmSignUp function with cognito_sub support
    const handleConfirmSignUp = async () => {
        try {
            setLoadingConfirmSignup(true);
            
            const { isSignUpComplete, nextStep } = await confirmSignUp({ 
                username: email, 
                confirmationCode 
            });
            
            console.log('✅ Email confirmation successful');
            
            // Wait a moment for Cognito to fully process the confirmation
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            // Get the cognito_sub from the authenticated session
            const cognitoSub = await getCognitoSub();
            
            if (!cognitoSub) {
                console.warn('⚠️ Could not retrieve cognito_sub - user creation may not be optimal');
            }
            
            const userData = { 
                firstName: firstName, 
                lastName: lastName, 
                email: email, 
                signup_time: getMysqlUtcDatetime(),
                cognito_sub: cognitoSub // Include cognito_sub in userData
            };

            // Check for pending invitation first
            const pendingInvite = localStorage.getItem('pendingInviteToken');
            let invitationProcessed = false;
            
            if (pendingInvite) {
                console.log('📨 Processing pending invitation...');
                invitationProcessed = await processPendingInvitation(pendingInvite, email, userData, cognitoSub);
                localStorage.removeItem('pendingInviteToken');
            }
            
            // Only add user normally if invitation wasn't processed successfully
            if (!invitationProcessed) {
                console.log('➕ Adding user to Users table...');
                const userAdded = await addNewUser(userData);
                
                if (!userAdded) {
                    console.warn('⚠️ User creation failed, but continuing...');
                }
            }

            setLoadingConfirmSignup(false);

            let successMsg = invitationProcessed ? 
                "Account created and invitation accepted! You can now sign in." :
                "Your sign up was successful. Sign in to continue.";
            
            console.log(successMsg);
            notifySuccess(successMsg);

            // Call the completion callback if provided (for invitation flow)
            if (onSignUpComplete) {
                onSignUpComplete({ 
                    email, 
                    firstName, 
                    lastName, 
                    cognitoSub: cognitoSub 
                });
            } else {
                onClose();
            }

        } catch (error) {
            console.error('❌ Error confirming sign up:', error.message);
            setLoadingConfirmSignup(false);
            
            // Handle specific Cognito errors
            if (error.message.includes('Invalid verification code')) {
                notifyError('Invalid verification code. Please check and try again.');
            } else if (error.message.includes('CodeExpiredException')) {
                notifyError('Verification code has expired. Please request a new one.');
            } else {
                notifyError(error.message || 'Failed to verify email. Please try again.');
            }
        }
    };

    const isInvitation = !!prefilledEmail;

    return (
        <>
            {!isConfirmed ? (
                <div className='signup-form-container'>
                    <div className='auth-header'>
                        <h1 className='auth-title'>Create account</h1>
                        <p className='auth-subtitle'>Get started with your feedback journey</p>
                    </div>

                    {isInvitation && (
                        <div className='notification-banner'>
                            💡 This email was provided from your invitation link
                        </div>
                    )}

                    <form onSubmit={handleSignUp}>
                        <div className='name-row'>
                            <div className='form-group'>
                                <label className='form-label' htmlFor="first-name">First name</label>
                                <input 
                                    id="first-name"
                                    className='form-input'
                                    required
                                    type="text"
                                    placeholder="John"
                                    value={firstName}
                                    onChange={handleFirstNameChange}
                                /> 
                            </div>

                            <div className='form-group'>
                                <label className='form-label' htmlFor="last-name">Last name</label>
                                <input 
                                    id="last-name"
                                    className='form-input'                 
                                    type="text"
                                    placeholder="Doe"
                                    value={lastName}
                                    onChange={handleLastNameChange}
                                />   
                            </div>
                        </div>
                        
                        <div className='form-group'>
                            <label className='form-label' htmlFor="signup-email">Email address</label>
                            <input 
                                id="signup-email"
                                className={`form-input ${isInvitation ? 'readonly' : ''}`}              
                                required
                                type="email"
                                placeholder="john@company.com"
                                value={email}
                                onChange={handleEmailChange}
                                disabled={isInvitation}
                                readOnly={isInvitation}
                            />   
                        </div>

                        <div className='form-group'>
                            <label className='form-label' htmlFor="signup-password">Password</label>
                            <div className="password-input-wrapper">
                                <input 
                                    id="signup-password"
                                    className='form-input' 
                                    required
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Create a strong password"
                                    minLength={8}
                                    value={password}
                                    onChange={handlePasswordChange}
                                />
                                <button 
                                    type="button"
                                    className="password-toggle"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    <FontAwesomeIcon icon={showPassword ? faEye : faEyeSlash} />
                                </button>
                            </div>
                        </div>
                        
                        <button 
                            type='submit' 
                            className='submit-btn'
                            disabled={loadingSignup}
                        >
                            {loadingSignup ? (
                                <>
                                    <div className="loading-spinner"></div>
                                    Creating Account...
                                </>
                            ) : 'Create Account'}
                        </button>
                    </form>
                </div>
            ) : (
                <div className='email-verify-section'>
                    <div className='auth-header'>
                        <h1 className='auth-title'>Check your email</h1>
                        <p className='auth-subtitle'>
                            We've sent a verification code to <span className='highlight-email'>{email}</span>
                        </p>
                    </div>

                    {isInvitation && (
                        <div className='notification-banner success'>
                            🎉 After verification, your invitation will be automatically accepted!
                        </div>
                    )}

                    <div className='form-group'>
                        <label className='form-label' htmlFor="verification-code">Verification code</label>
                        <input 
                            id="verification-code"
                            className='form-input verification-input'
                            type="text"
                            placeholder="Enter verification code"
                            value={confirmationCode}
                            onChange={(e) => setConfirmationCode(e.target.value)}
                        />
                    </div>

                    <button 
                        className='submit-btn' 
                        onClick={handleConfirmSignUp}
                        disabled={loadingConfirmSignup}
                    >
                        {loadingConfirmSignup ? (
                            <>
                                <div className="loading-spinner"></div>
                                Verifying...
                            </>
                        ) : 'Verify Email'}
                    </button>

                    <div className='auth-links'>
                        <button 
                            type="button"
                            className='auth-link-btn' 
                            onClick={() => resendConfirmationCode(email)}
                        >
                            Didn't receive the code? Resend
                        </button>
                    </div>
                </div>
            )}
        </>
    )
}

export default SignUp;